import { z } from 'zod';
import { componentKeySchema } from '../contracts/component.ts';
import {
  componentDescriptorSchema,
  projectCommandSchema,
} from '../contracts/index.ts';
import { jsonObjectSchema, jsonValueSchema } from '../contracts/json.ts';

// ─── Category ────────────────────────────────────────────────────────────────

export const templateCategorySchema = z.enum([
  'model',
  'scene',
  'layout',
  'interaction',
  'project',
]);

export type TemplateCategory = z.infer<typeof templateCategorySchema>;

// ─── Parameter Definition ───────────────────────────────────────────────────

export const templateParameterTypeSchema = z.enum([
  'string',
  'number',
  'boolean',
  'enum',
  'json',
]);

export const templateParameterEnumOptionSchema = z.object({
  value: z.string().min(1),
  label: z.string().min(1),
  description: z.string().default(''),
}).strict();

export const templateParameterSchema = z.object({
  name: z.string().min(1),
  type: templateParameterTypeSchema,
  description: z.string().default(''),
  /**
   * guidance=true 表示参数只作为 Agent 内容/教学规划参考，
   * 当前 Layout Kernel 不承诺消费它。
   */
  guidance: z.boolean().default(false),
  required: z.boolean().default(false),
  defaultValue: jsonValueSchema.optional(),
  enumOptions: z.array(templateParameterEnumOptionSchema).max(100).default([]),
  constraints: z.array(z.string().min(1)).max(50).default([]),
}).strict().superRefine((param, context) => {
  if (param.type === 'enum' && param.enumOptions.length === 0) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['enumOptions'],
      message: 'enum 类型参数必须提供至少一个 enumOptions',
    });
  }
});

export type TemplateParameterType = z.infer<typeof templateParameterTypeSchema>;
export type TemplateParameterEnumOption = z.infer<
  typeof templateParameterEnumOptionSchema
>;
export type TemplateParameter = z.infer<typeof templateParameterSchema>;

// ─── Slot Definition ────────────────────────────────────────────────────────

export const slotRoleSchema = z.enum([
  'primary',
  'analysis',
  'summary',
  'controls',
  'explanation',
  'media',
]);

export const slotDefinitionSchema = z.object({
  id: z.string().min(1),
  role: slotRoleSchema,
  description: z.string().default(''),
  required: z.boolean().default(false),
  minCount: z.number().int().nonnegative().default(0),
  maxCount: z.number().int().positive().default(1),
  allowedComponentKeys: z.array(componentKeySchema).max(200).default([]),
  allowedCapabilities: z.array(z.string().min(1)).max(200).default([]),
}).strict().superRefine((slot, context) => {
  if (slot.maxCount < slot.minCount) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['maxCount'],
      message: 'maxCount 不能小于 minCount',
    });
  }
});

export type SlotRole = z.infer<typeof slotRoleSchema>;
export type SlotDefinition = z.infer<typeof slotDefinitionSchema>;

// ─── Template Example ───────────────────────────────────────────────────────

export const templateExampleSchema = z.object({
  description: z.string().min(1),
  params: jsonObjectSchema.default({}),
  slots: jsonObjectSchema.default({}),
}).strict();

export type TemplateExample = z.infer<typeof templateExampleSchema>;

// ─── Base Template Descriptor ───────────────────────────────────────────────

export const templateDescriptorSchema = z.object({
  id: z.string().min(1).max(200),
  version: z.string().min(1),
  title: z.string().min(1),
  category: templateCategorySchema,
  description: z.string().default(''),
  suitableFor: z.array(z.string().min(1)).max(100).default([]),
  requiredCapabilities: z.array(z.string().min(1)).max(200).default([]),
  parameters: z.array(templateParameterSchema).max(100).default([]),
  slots: z.array(slotDefinitionSchema).max(100).default([]),
  constraints: z.array(z.string().min(1)).max(100).default([]),
  defaults: jsonObjectSchema.default({}),
  examples: z.array(templateExampleSchema).max(20).default([]),
}).strict();

export type TemplateDescriptor = z.infer<typeof templateDescriptorSchema>;

// ─── Layout-specific Descriptor ─────────────────────────────────────────────

export const responsiveDensitySchema = z.enum(['compact', 'regular', 'expanded']);

export const layoutBreakpointSchema = z.object({
  name: z.string().min(1),
  minWidth: z.number().nonnegative().finite(),
  maxWidth: z.number().positive().finite().optional(),
  columns: z.number().int().positive().default(1),
  gap: z.number().nonnegative().finite().default(16),
  mode: z.enum(['freeform', 'grid', 'stack']).default('grid'),
  columnRatios: z.array(z.number().positive().max(1)).max(100).optional(),
}).strict().superRefine((bp, context) => {
  if (bp.maxWidth !== undefined && bp.maxWidth < bp.minWidth) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['maxWidth'],
      message: 'maxWidth 不能小于 minWidth',
    });
  }
});

export const layoutTemplateDescriptorSchema = templateDescriptorSchema.extend({
  category: z.literal('layout'),
  breakpoints: z.array(layoutBreakpointSchema).max(20).default([]),
  preferredDensity: responsiveDensitySchema.default('regular'),
}).strict();

export type ResponsiveDensity = z.infer<typeof responsiveDensitySchema>;
export type LayoutBreakpoint = z.infer<typeof layoutBreakpointSchema>;
export type LayoutTemplateDescriptor = z.infer<
  typeof layoutTemplateDescriptorSchema
>;

// ─── Materialize Input ──────────────────────────────────────────────────────

export const slotAssignmentSchema = z.object({
  slotId: z.string().min(1),
  componentKey: componentKeySchema,
  config: jsonObjectSchema.default({}),
}).strict();

export const materializeInputSchema = z.object({
  templateId: z.string().min(1),
  params: jsonObjectSchema.default({}),
  slotAssignments: z.array(slotAssignmentSchema).max(200).default([]),
  containerWidth: z.number().positive().finite().default(1440),
  containerHeight: z.number().positive().finite().default(900),
  componentDescriptors: z.array(componentDescriptorSchema).max(500).optional(),
  projectId: z.string().min(1),
  pageId: z.string().min(1),
  pageTitle: z.string().max(100000).optional(),
}).strict();

export type SlotAssignment = z.infer<typeof slotAssignmentSchema>;
export type MaterializeInput = z.input<typeof materializeInputSchema>;

// ─── Materialize Output ─────────────────────────────────────────────────────

export const materializeResultSchema = z.object({
  commands: z.array(projectCommandSchema).min(0),
  diagnostics: z.array(
    z.object({
      severity: z.enum(['info', 'warn', 'error']),
      code: z.string().min(1),
      path: z.string().default(''),
      message: z.string().min(1),
    }).strict(),
  ).default([]),
}).strict();

export type MaterializeResult = z.infer<typeof materializeResultSchema>;
